"use client";

import { useAuthRequiredModal } from "@/components/auth/auth-required-modal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { usePropertyComments } from "@/hooks/use-property-comments";
import { useUser } from "@/hooks/useUser";
import { ArrowRight, Heart, MessageCircle, Trash2, Users } from "lucide-react";
import { useState } from "react";

interface PropertyDiscussionProps {
  analysisId: string;
  address: string;
}

export function PropertyDiscussion({
  analysisId,
  address,
}: PropertyDiscussionProps) {
  const {
    comments,
    isLoading,
    createComment,
    toggleLike,
    deleteComment,
    isDeleting,
  } = usePropertyComments({
    analysisId,
  });
  const [newComment, setNewComment] = useState("");
  const { user } = useUser();
  const { toast } = useToast();
  const { requireAuth, AuthRequiredModal } = useAuthRequiredModal();

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      if (!user) {
        try {
          await requireAuth();
        } catch {
          toast({
            title: "Comment not submitted",
            description: "You need to be signed in to leave a comment.",
            variant: "destructive",
          });
          return;
        }
      }

      await createComment({ content: newComment });
      setNewComment("");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit comment. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleLikeToggle = async (commentId: string, isLiked: boolean) => {
    try {
      if (!user) {
        try {
          await requireAuth();
        } catch {
          toast({
            title: "Action not completed",
            description: "You need to be signed in to like comments.",
            variant: "destructive",
          });
          return;
        }
      }

      await toggleLike({ commentId, isLiked });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update like status. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteComment({ commentId });
      toast({
        title: "Success",
        description: "Comment deleted successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete comment. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <section className="relative" id="property-discussion">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-semibold flex items-center gap-2">
          <MessageCircle className="h-6 w-6 text-primary" />
          Property Discussion
        </h2>
        {!isLoading && comments.length !== 0 && (
          <span className="text-sm text-muted-foreground">
            {comments.length} comment{comments.length > 1 ? "s" : ""}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="py-12 flex flex-col items-center justify-center text-center">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Users className="h-6 w-6 text-primary" />
          </div>
          <h4 className="text-lg font-medium mb-2">Loading...</h4>
          <p className="text-muted-foreground max-w-[420px] mb-8">
            Loading comments...
          </p>
        </div>
      ) : comments.length === 0 ? (
        <div className="py-12 flex flex-col items-center justify-center text-center">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Users className="h-6 w-6 text-primary" />
          </div>
          <h4 className="text-lg font-medium mb-2">Start the Conversation</h4>
          <p className="text-muted-foreground max-w-[420px] mb-8">
            Share your insights on this property—did you visit the open house?
            Know of any hidden pros or cons? Your street-level knowledge could
            be exactly what another buyer needs.
          </p>
          <div className="w-full max-w-[600px]">
            <div className="flex gap-4">
              <Avatar className="h-10 w-10 shrink-0">
                <AvatarFallback>You</AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-4">
                <Textarea
                  placeholder={`What's your take on ${address ?? "this property"}?`}
                  className="resize-none min-h-[100px]"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                />
                <div className="flex justify-end">
                  <Button onClick={handleSubmitComment}>
                    Start Discussion
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Comment Input */}
          <div className="flex gap-4">
            <Avatar className="h-10 w-10 shrink-0">
              <AvatarFallback>You</AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-4">
              <Textarea
                placeholder="Share your insights about this property..."
                className="resize-none min-h-[100px]"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
              <div className="flex justify-end">
                <Button onClick={handleSubmitComment}>
                  Comment
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Comments List */}
          <div className="space-y-8">
            {comments.map((comment) => (
              <div key={comment.id} className="group">
                <div className="flex gap-4">
                  <Avatar className="h-10 w-10 shrink-0">
                    {comment.user && (
                      <AvatarImage
                        src={comment.user.avatar_url ?? ""}
                        alt={`${comment.user.first_name ?? ""} ${comment.user.last_name ?? ""}`}
                      />
                    )}
                    <AvatarFallback>
                      {comment.user?.first_name?.[0]}
                      {comment.user?.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">
                        {comment.user?.first_name} {comment.user?.last_name}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {new Date(comment.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      {comment.content}
                    </p>
                    <div className="flex items-center gap-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-8 px-3 -ml-3 ${
                          comment.isLiked ? "text-primary" : ""
                        }`}
                        onClick={() =>
                          handleLikeToggle(comment.id, comment.isLiked)
                        }
                      >
                        <Heart
                          className={`mr-1.5 h-4 w-4 ${
                            comment.isLiked ? "fill-current" : ""
                          }`}
                        />
                        {comment.likes > 0 && comment.likes}
                      </Button>
                      {user?.id === comment.user_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-3 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDeleteComment(comment.id)}
                          disabled={isDeleting}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AuthRequiredModal
        title="Sign in to join the discussion"
        description="Sign in to leave comments and interact with other users"
      />
    </section>
  );
}
